import { base64ToBlob, isValidBase64Image } from '@/utils';

export interface ProcessedImage {
  blob: Blob;
  objectUrl: string;
  mimeType: string;
}

/**
 * Service for handling image processing and conversion between different formats
 */
export class ImageProcessingService {
  private static readonly objectUrls: Set<string> = new Set();

  /**
   * Process a single base64 image string and convert it to all needed formats
   * @param base64String - The base64 image string
   * @returns Promise<ProcessedImage> - Processed image with all formats
   */
  static async processImage(base64String: string): Promise<ProcessedImage> {
    if (!isValidBase64Image(base64String)) {
      throw new Error('Invalid base64 image string');
    }

    // Extract MIME type if it's a data URI
    let mimeType = 'image/png'; // default
    if (base64String.startsWith('data:')) {
      const mimeMatch = /data:([^;]+)/.exec(base64String);
      if (mimeMatch) {
        mimeType = mimeMatch[1];
      }
    }

    // Convert to blob
    const blob = await base64ToBlob(base64String, mimeType);
    
    // Create object URL for display
    const objectUrl = URL.createObjectURL(blob);
    this.objectUrls.add(objectUrl);

    return {
      blob,
      objectUrl,
      mimeType
    };
  }

  /**
   * Process multiple base64 image strings
   * @param base64Images - Array of base64 image strings
   * @returns Promise<ProcessedImage[]> - Array of processed images
   */
  static async processImages(base64Images: string[]): Promise<ProcessedImage[]> {
    const processingPromises = base64Images.map(base64String => 
      this.processImage(base64String)
    );
    return Promise.all(processingPromises);
  }

  /**
   * Clean up object URLs to prevent memory leaks
   * @param urls - Array of object URLs to revoke
   */
  static revokeObjectUrls(urls: string[]): void {
    urls.forEach(url => {
      if (this.objectUrls.has(url)) {
        URL.revokeObjectURL(url);
        this.objectUrls.delete(url);
      }
    });
  }

  /**
   * Clean up all created object URLs
   */
  static revokeAllObjectUrls(): void {
    this.objectUrls.forEach(url => URL.revokeObjectURL(url));
    this.objectUrls.clear();
  }

  /**
   * Read mock images from the mock file and convert them to processed images
   * @returns Promise<ProcessedImage[]> - Array of processed mock images
   */
  static async loadMockImages(): Promise<ProcessedImage[]> {
    // Load the base64 images from mock_image.txt as a string
    const response = await fetch('/src/mocks/mock_image.txt');
    const mockFileContent = await response.text();
    // Split by newlines and filter out empty lines
    const mockBase64Images = mockFileContent.split('\n').filter(line => line.trim().length > 0);
    return this.processImages(mockBase64Images);
  }

  /**
   * Convert a File to base64 string
   * @param file - The File object to convert
   * @returns Promise<string> - Base64 string representation
   */
  static fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        } else {
          reject(new Error('Failed to read file as base64'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
    });
  }

  /**
   * Convert multiple Files to base64 strings with metadata
   * @param files - Array of File objects to convert
   * @returns Promise<Array<{file: File, base64: string, metadata: any}>>
   */
  static async filesToBase64WithMetadata(files: File[]): Promise<{ file: File; base64: string; metadata: any }[]> {
    const results = await Promise.all(
      files.map(async (file) => {
        const base64 = await this.fileToBase64(file);
        const metadata = {
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified
        };
        
        return {
          file,
          base64,
          metadata
        };
      })
    );

    return results;
  }
}
